import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useRoute } from '../../context/RouteContext';
import { useAuth } from '../../context/AuthContext';
import { fetchRoutePOIs, fetchRouteDiscoveries } from '../../services/poiService';
import { analyzeFuelRange, projectAlongRoute } from '../../utils/poi';
import { panToPoint } from '../../utils/mapController';
import { haversineKm } from '../../utils/geomath';
import './RoutePOI.css';

const ProModal = lazy(() => import('../ProModal/ProModal').then(m => ({ default: m.ProModal })));

const FUEL_GAP_WARN_KM = 150;
const km = (v) => `${Math.round(v)} km`;

const CATS = [
  { key: 'fuel',      label: 'Fuel',  icon: '⛽' },
  { key: 'viewpoint', label: 'Views', icon: '🏞️' }, // U+FE0F → render as colour emoji
  { key: 'food',      label: 'Food',  icon: '☕' },
];

export function RoutePOI() {
  const { state, dispatch } = useRoute();
  const { isSubscribed } = useAuth();
  const { geometry, waypoints } = state.current;
  const { pois, poiPins } = state;

  const hasRoute = waypoints.length >= 2 && (geometry?.coordinates?.length ?? 0) >= 2;
  const sig = hasRoute ? `${geometry.coordinates.length}:${geometry.coordinates[0]}:${geometry.coordinates[geometry.coordinates.length - 1]}` : '';

  const [loading, setLoading] = useState(false);
  const [failed, setFailed]   = useState(false);
  const [tab, setTab]         = useState('fuel');
  const [showPro, setShowPro] = useState(false);
  const [discoveries, setDiscoveries] = useState(null);
  const [added, setAdded] = useState(() => new Set());

  useEffect(() => {
    if (!isSubscribed || !hasRoute || !sig) { dispatch({ type: 'SET_POIS', payload: null }); return; }
    const ctl = new AbortController();
    setLoading(true);
    setFailed(false);
    fetchRoutePOIs(geometry, ctl.signal)
      .then(data => {
        if (ctl.signal.aborted) return;
        if (data) dispatch({ type: 'SET_POIS', payload: data }); else setFailed(true);
      })
      .finally(() => { if (!ctl.signal.aborted) setLoading(false); });
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, isSubscribed]);

  // "Worth a stop" discoveries (Wikipedia) — independent of the POI fetch.
  // Shared via context so the map can render hoverable discovery markers.
  useEffect(() => {
    if (!isSubscribed || !hasRoute || !sig) {
      setDiscoveries(null);
      dispatch({ type: 'SET_DISCOVERIES', payload: null });
      return;
    }
    const ctl = new AbortController();
    setDiscoveries(null);
    setAdded(new Set());
    fetchRouteDiscoveries(geometry, ctl.signal)
      .then(places => {
        if (ctl.signal.aborted) return;
        setDiscoveries(places || []);
        dispatch({ type: 'SET_DISCOVERIES', payload: places || [] });
      })
      .catch(() => { if (!ctl.signal.aborted) setDiscoveries([]); });
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, isSubscribed]);

  // Discoveries projected onto the route → km along the ride, sorted.
  const discList = useMemo(() => {
    if (!discoveries?.length || !hasRoute) return discoveries;
    return projectAlongRoute(geometry.coordinates, discoveries);
  }, [discoveries, geometry, hasRoute]);

  // "Already a stop" derived from the actual waypoints (survives the route
  // re-computing after an add, which resets the optimistic `added` set).
  const addedIds = useMemo(() => {
    const ids = new Set(added);
    if (!discList?.length) return ids;
    for (const place of discList) {
      if (waypoints.some(wp =>
        wp.lat != null && haversineKm(wp.lat, wp.lng, place.lat, place.lng) < 0.15
      )) ids.add(place.id);
    }
    return ids;
  }, [discList, waypoints, added]);

  // Insert the stop at its position ALONG the route (not at the end), so the
  // existing ride order is preserved and only the local leg reshapes.
  function addStop(place) {
    if (addedIds.has(place.id)) return;
    const coords = geometry.coordinates;
    const wpKms = waypoints.map(wp =>
      wp.lat != null ? projectAlongRoute(coords, [wp])[0].km : Infinity
    );
    let index = wpKms.findIndex(k => k > (place.km ?? 0));
    if (index === -1) index = waypoints.length - 1;          // near the end → before the finish
    index = Math.min(Math.max(index, 1), waypoints.length - 1); // never displace start/finish
    dispatch({ type: 'INSERT_WAYPOINT', payload: { index, lat: place.lat, lng: place.lng, name: place.title } });
    setAdded(prev => new Set(prev).add(place.id));
  }

  // Clicking a POI row pans the map there (zooming in if needed to escape a
  // marker cluster), shows the category pins so there's something to see when
  // we arrive, and highlights that marker so it's obvious which one it is.
  function focusPoi(p, i) {
    if (p.lat == null) return;
    if (!poiPins[tab]) dispatch({ type: 'TOGGLE_POI_PIN', payload: tab });
    dispatch({ type: 'SET_SELECTED_MAP_ITEM', payload: { kind: 'poi', cat: tab, i } });
    panToPoint(p.lat, p.lng, { zoom: 14 });
  }

  // Same idea for a "worth a stop" card — pan there and highlight its marker.
  function focusDiscovery(place) {
    if (place.lat == null) return;
    if (!poiPins.discoveries) dispatch({ type: 'TOGGLE_POI_PIN', payload: 'discoveries' });
    dispatch({ type: 'SET_SELECTED_MAP_ITEM', payload: { kind: 'discovery', id: place.id } });
    panToPoint(place.lat, place.lng, { zoom: 14 });
  }

  // Project each category onto the route for sorted, km-stamped lists.
  const lists = useMemo(() => {
    if (!pois || !hasRoute) return null;
    const coords = geometry.coordinates;
    return {
      fuel:      projectAlongRoute(coords, pois.fuel),
      viewpoint: projectAlongRoute(coords, pois.viewpoint),
      food:      projectAlongRoute(coords, pois.food),
    };
  }, [pois, geometry, hasRoute]);

  const fuelRange = useMemo(
    () => (pois && hasRoute ? analyzeFuelRange(geometry.coordinates, pois.fuel) : null),
    [pois, geometry, hasRoute]
  );

  if (!hasRoute) return null;

  // ── Free: upsell teaser ──
  if (!isSubscribed) {
    return (
      <div className="rpoi rpoi--teaser">
        <div className="rpoi__head"><span className="rpoi__title">Fuel &amp; stops en route</span><span className="rpoi__lock" aria-hidden="true">🔒</span></div>
        <p className="rpoi__teaser-text">
          Browse fuel stations, viewpoints and food along your ride, drop them on
          the map, and get a fuel-range warning for long gaps between stations.
        </p>
        <button className="rpoi__upsell" onClick={() => setShowPro(true)}>Unlock with Pro</button>
        {showPro && <Suspense fallback={null}><ProModal onClose={() => setShowPro(false)} /></Suspense>}
      </div>
    );
  }

  // Fuel-range headline.
  let status = null, level = 'ok';
  if (pois) {
    if (!pois.fuel.length) { level = 'warn'; status = 'No fuel stations detected — fill up before you go.'; }
    else if (fuelRange && fuelRange.maxGap >= FUEL_GAP_WARN_KM) { level = 'warn'; status = `⛽ Longest stretch without fuel: ${km(fuelRange.maxGap)} (from ${km(fuelRange.gapStartKm)})`; }
    else if (fuelRange) { level = 'ok'; status = `⛽ Fuel well-spaced — longest gap ${km(fuelRange.maxGap)}`; }
  }

  const activeList = lists?.[tab] || [];

  return (
    <div className="rpoi">
      <div className="rpoi__head"><span className="rpoi__title">Fuel &amp; stops en route</span></div>

      {loading && !pois && (
        <div className="rpoi__skeleton"><div className="rpoi__sk-line" /><div className="rpoi__sk-row" /></div>
      )}
      {failed && !pois && <div className="rpoi__error">Couldn’t load points of interest — try again shortly.</div>}

      {pois && (
        <>
          {status && <div className={`rpoi__status rpoi__status--${level}`}>{status}</div>}

          {/* Category tabs */}
          <div className="rpoi__tabs">
            {CATS.map(c => (
              <button
                key={c.key}
                className={`rpoi__tab${tab === c.key ? ' rpoi__tab--active' : ''}`}
                onClick={() => setTab(c.key)}
              >
                <span className="rpoi__tab-ic">{c.icon}</span>
                {lists?.[c.key]?.length ?? 0} {c.label}
              </button>
            ))}
          </div>

          {/* Show-on-map toggle for the active category */}
          <label className="rpoi__pin-toggle">
            <input
              type="checkbox"
              checked={!!poiPins[tab]}
              onChange={() => dispatch({ type: 'TOGGLE_POI_PIN', payload: tab })}
            />
            <span>Show {CATS.find(c => c.key === tab)?.label.toLowerCase()} pins on map</span>
          </label>

          {/* Scrollable list */}
          {activeList.length === 0 ? (
            <div className="rpoi__empty">None found along this route.</div>
          ) : (
            <div className="rpoi__list">
              {activeList.map((p, i) => (
                <button key={i} className="rpoi__item rpoi__item--btn" onClick={() => focusPoi(p, i)} title="Show on map">
                  <span className="rpoi__item-km">{km(p.km)}</span>
                  <span className="rpoi__item-name">{p.name || (tab === 'fuel' ? 'Fuel station' : tab === 'viewpoint' ? 'Viewpoint' : 'Food stop')}</span>
                  {p.offKm > 0.15 && <span className="rpoi__item-off">{p.offKm < 1 ? `${Math.round(p.offKm * 1000)} m` : `${p.offKm.toFixed(1)} km`} off</span>}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Worth a stop: notable places along the route (Wikipedia) ── */}
      {discoveries && discoveries.length > 0 && (
        <div className="rpoi__disc">
          <div className="rpoi__disc-head">
            <span className="rpoi__disc-title">Worth a stop</span>
            <span className="rpoi__disc-sub">{discoveries.length} places along this route</span>
          </div>
          <label className="rpoi__pin-toggle">
            <input
              type="checkbox"
              checked={!!poiPins.discoveries}
              onChange={() => dispatch({ type: 'TOGGLE_POI_PIN', payload: 'discoveries' })}
            />
            <span>Show these pins on map</span>
          </label>
          <div className="rpoi__disc-strip">
            {discList.map(place => (
              <div
                key={place.id}
                className="rpoi__disc-card"
                onClick={() => focusDiscovery(place)}
                title="Show on map"
              >
                <a href={place.url} target="_blank" rel="noopener noreferrer" title={`Read about ${place.title} on Wikipedia`}>
                  <img className="rpoi__disc-img" src={place.thumb} alt={place.title} loading="lazy" />
                  {place.km != null && <span className="rpoi__disc-km">at {km(place.km)}</span>}
                </a>
                <div className="rpoi__disc-body">
                  <div className="rpoi__disc-name">{place.title}</div>
                  {place.extract && <p className="rpoi__disc-desc">{place.extract}</p>}
                  <button
                    className={`rpoi__disc-add${addedIds.has(place.id) ? ' rpoi__disc-add--done' : ''}`}
                    onClick={() => addStop(place)}
                    disabled={addedIds.has(place.id)}
                  >
                    {addedIds.has(place.id) ? '✓ Added to route' : '+ Add as stop'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <span className="rpoi__disc-credit">Photos &amp; summaries from Wikipedia</span>
        </div>
      )}
    </div>
  );
}
